/*
  Three-For-Two Handler - Spanish Version

  Features:
  - Correctly handles partial groups
  - Updates dynamically when quantities change
  - Properly saves and restores the original state
  - Checks correctly for valid codes and notices when code not present
  - Adapted for Spanish market with Euro currency and comma decimal separator
*/

window.ThreeForTwoHandler = (function () {
  let discountActive = false;
  let currentDiscountCode = null;
  let activeItemIds = new Map();
  let debug = false;
  let processingUpdate = false;
  let isUpdatingProperties = false;
  let itemsWithProperties = new Set();

  function log(...args) {
    if (debug) {
      console.log('[ThreeForTwoHandler]', ...args);
    }
  }

  function isValid3For2Code(code) {
    if (!code || !window.active3for2codes?.buyXgetYcodes) return false;
    return window.active3for2codes.buyXgetYcodes.includes(code.trim().toUpperCase());
  }

  function saveOriginalPriceHTML(itemId) {
    const itemElement = document.querySelector(`[data-item-id="${itemId}"]`);
    if (!itemElement) return false;
    const priceContainer = itemElement.querySelector('.rebuy-cart__flyout-item-price');
    if (!priceContainer) return false;
    if (!priceContainer.hasAttribute('data-original-html')) {
      priceContainer.setAttribute('data-original-html', priceContainer.innerHTML);
      log('Saved original HTML for item:', itemId);
    }
    return true;
  }

  function updateItemPrice(itemData) {
    const { id, freeQty, totalQty, price } = itemData;
    const itemElement = document.querySelector(`[data-item-id="${id}"]`);
    if (!itemElement) return false;
    const priceContainer = itemElement.querySelector('.rebuy-cart__flyout-item-price');
    if (!priceContainer) return false;

    const paidQty = Math.max(0, totalQty - freeQty);
    const paidTotal = (price * paidQty).toFixed(2).replace('.', ',');
    const fullTotal = (price * totalQty).toFixed(2).replace('.', ',');

    const priceHTML =
      freeQty >= totalQty
        ? `
      <div>
        <span class="rebuy-money sale"><span class="sr-only">Discounted Price</span><span>€ 0,00</span></span>
        <span class="rebuy-money compare-at"><span class="sr-only">Original Price</span><span>€ ${fullTotal}</span></span>
      </div>`
        : `
      <div>
        <span class="rebuy-money sale"><span class="sr-only">Discounted Price</span><span>€ ${paidTotal}</span></span>
        <span class="rebuy-money compare-at"><span class="sr-only">Original Price</span><span>€ ${fullTotal}</span></span>
      </div>`;

    priceContainer.innerHTML = priceHTML;
    itemElement.setAttribute('data-three-for-two-free', freeQty);
    itemElement.setAttribute('data-three-for-two-total', totalQty);
    log('Updated price display for item:', id);
    return true;
  }

  function addPromoBanner() {
    // Only add banner if it doesn't already exist
    if (document.getElementById('three-for-two-banner')) {
      return;
    }

    log('Adding 3-for-2 promo banner to cart');

    // Find the cart items container first
    const cartItemsContainer = document.querySelector('[data-rebuy-component="cart-items"]');
    if (!cartItemsContainer) {
      log('Cart items container not found');
      return;
    }

    // Create the banner element
    const banner = document.createElement('div');
    banner.id = 'three-for-two-banner';
    banner.style.backgroundColor = '#f8f4f4';
    banner.style.color = 'palevioletred'; // Pink color instead of red
    banner.style.padding = '10px 15px';
    banner.style.margin = '0 0 10px 0';
    banner.style.borderRadius = '4px';
    banner.style.fontWeight = 'bold';
    banner.style.textAlign = 'center';
    banner.style.fontSize = '14px';
    banner.style.borderLeft = '3px solid #ff69b4'; // Pink border
    banner.style.zIndex = '1000';
    // Add the banner text - Italian version
    banner.innerHTML = 'Promoción 3x2: ¡El artículo menos costoso es gratis!';

    // Insert before the cart items
    cartItemsContainer.parentNode.insertBefore(banner, cartItemsContainer);

    log('Promo banner added');
  }

  // Remove the banner if it exists
  function removePromoBanner() {
    const banner = document.getElementById('three-for-two-banner');
    if (banner) {
      banner.remove();
      log('Promo banner removed');
    }
  }

  // Highlight free items with a visual indication directly in the title
  function highlightFreeItems(freeItemsData) {
    // First remove any existing highlights
    document.querySelectorAll('.three-for-two-highlight').forEach(el => {
      el.remove();
    });

    // Then add highlights to the free items
    freeItemsData.forEach(itemData => {
      const { id } = itemData;
      const itemElement = document.querySelector(`[data-item-id="${id}"]`);
      if (!itemElement) return;

      // Find the variant title element (just after the product title)
      const variantTitleElement = itemElement.querySelector(
        '.rebuy-cart__flyout-item-variant-title'
      );
      if (!variantTitleElement) return;

      // Create the highlight element
      const highlight = document.createElement('span');
      highlight.classList.add('three-for-two-highlight');
      highlight.style.backgroundColor = 'palevioletred'; // Pink background
      highlight.style.color = 'white';
      highlight.style.padding = '2px 6px';
      highlight.style.borderRadius = '3px';
      highlight.style.marginLeft = '5px';
      highlight.style.fontSize = '11px';
      highlight.style.fontWeight = 'bold';
      highlight.style.display = 'inline-block';
      highlight.textContent = 'GRATIS'; // Same in Spanish

      // Add to the variant title
      variantTitleElement.appendChild(highlight);
      log('Added highlight to item:', id);
    });
  }

  // Remove all highlights
  function removeHighlights() {
    document.querySelectorAll('.three-for-two-highlight').forEach(el => {
      el.remove();
    });
    log('Removed all highlights');
  }

  function calculateDiscounts(cart) {
    if (!discountActive || !currentDiscountCode || !cart || cart.items.length < 3) return [];

    const flatItems = [];
    let totalPricedItems = 0;

    cart.items.forEach(item => {
      if (parseFloat(item.price) > 0) {
        totalPricedItems += item.quantity;
      }
    });

    const completeGroupCount = Math.floor(totalPricedItems / 3);
    log('Total items:', totalPricedItems, 'Complete groups:', completeGroupCount);
    if (completeGroupCount === 0) return [];

    cart.items.forEach((item, index) => {
      if (parseFloat(item.price) <= 0) return;
      for (let i = 0; i < item.quantity; i++) {
        flatItems.push({
          id: item.id,
          price: parseInt(item.price, 10),
          originalIndex: index,
          unitIndex: i,
        });
      }
    });

    flatItems.sort((a, b) => {
      if (a.price !== b.price) return a.price - b.price;
      return b.originalIndex - a.originalIndex;
    });

    const itemsForGroups = flatItems.slice(0, completeGroupCount * 3);
    const groups = [];
    for (let i = 0; i < itemsForGroups.length; i += 3) {
      groups.push(itemsForGroups.slice(i, i + 3));
    }

    log('Created', groups.length, 'complete groups from', itemsForGroups.length, 'items');
    groups.forEach((group, i) => {
      log('Group', i + 1, 'items:', group.map(item => `${item.id}:${item.price}`).join(', '));
    });

    const freeItems = new Map();
    groups.forEach((group, i) => {
      const freeItem = group[0];
      log('Free item in group', i + 1, ':', freeItem.id);
      if (!freeItems.has(freeItem.id)) {
        freeItems.set(freeItem.id, { freeQty: 1 });
      } else {
        const data = freeItems.get(freeItem.id);
        data.freeQty++;
        freeItems.set(freeItem.id, data);
      }
    });

    const result = [];
    for (const [id, data] of freeItems.entries()) {
      const item = cart.items.find(item => item.id === id);
      if (item) {
        const freeQty = Math.min(data.freeQty, item.quantity);
        result.push({
          id: id,
          freeQty: freeQty,
          totalQty: item.quantity,
          price: parseFloat(item.price) / 100,
          title: item.product?.title || 'Item',
        });
      }
    }

    log('Final free items:', result.map(i => `${i.id} (${i.freeQty} of ${i.totalQty})`).join(', '));
    return result;
  }

  function resetItemPrice(itemId) {
    const itemElement = document.querySelector(`[data-item-id="${itemId}"]`);
    if (!itemElement) return false;
    itemElement.removeAttribute('data-three-for-two-free');
    itemElement.removeAttribute('data-three-for-two-total');
    const priceContainer = itemElement.querySelector('.rebuy-cart__flyout-item-price');
    if (priceContainer && priceContainer.hasAttribute('data-original-html')) {
      priceContainer.innerHTML = priceContainer.getAttribute('data-original-html');
      priceContainer.removeAttribute('data-original-html');
      log('Restored original price for item:', itemId);
      return true;
    }
    return false;
  }

  function applyFreeItemUI(freeItemsData) {
    if (!discountActive || !currentDiscountCode) return;
    resetUI();
    if (freeItemsData.length === 0) return;

    // Save original prices and add to tracking
    freeItemsData.forEach(itemData => {
      saveOriginalPriceHTML(itemData.id);
      activeItemIds.set(itemData.id, itemData);
    });

    // Apply price updates
    freeItemsData.forEach(itemData => {
      updateItemPrice(itemData);
    });

    // Add the promo banner
    addPromoBanner();

    // Add highlights to free items
    highlightFreeItems(freeItemsData);

    log('Applied UI updates to', freeItemsData.length, 'items');
  }

  function resetUI() {
    log('Resetting UI');

    // Reset prices
    activeItemIds.forEach((_, id) => resetItemPrice(id));
    document.querySelectorAll('[data-three-for-two-free]').forEach(el => {
      const itemId = el.getAttribute('data-item-id');
      if (itemId) resetItemPrice(itemId);
    });

    // Remove banner and highlights
    const banner = document.getElementById('three-for-two-banner');
    if (banner) banner.remove();

    document.querySelectorAll('.three-for-two-highlight').forEach(el => {
      el.remove();
    });

    // Clear tracking
    activeItemIds.clear();
  }

  function processCart(cart) {
    if (!discountActive || !currentDiscountCode) {
      log('processCart skipped: discount not active');
      return;
    }
    if (processingUpdate) {
      log('Already processing an update, skipping');
      return;
    }
    // Skip processing if we're updating properties
    if (isUpdatingProperties) {
      log('Skipping cart processing while updating properties');
      return;
    }
    processingUpdate = true;
    try {
      log('Processing cart with discount active:', discountActive);
      if (!discountActive || !cart) {
        resetUI();
        return;
      }
      const freeItemsData = calculateDiscounts(cart);
      if (freeItemsData.length > 0) {
        applyFreeItemUI(freeItemsData);
      } else {
        resetUI();
      }
    } catch (error) {
      log('Error processing cart:', error);
    } finally {
      processingUpdate = false;
    }
  }

  function deactivateHandler() {
    log('Deactivating ThreeForTwoHandler');

    discountActive = false;
    currentDiscountCode = null;
    processingUpdate = false;

    // Remove data attributes
    document.querySelectorAll('[data-three-for-two-free]').forEach(el => {
      el.removeAttribute('data-three-for-two-free');
      el.removeAttribute('data-three-for-two-total');
    });

    // Restore original prices
    document.querySelectorAll('[data-item-id]').forEach(el => {
      const priceContainer = el.querySelector('.rebuy-cart__flyout-item-price');
      if (priceContainer && priceContainer.hasAttribute('data-original-html')) {
        priceContainer.innerHTML = priceContainer.getAttribute('data-original-html');
        priceContainer.removeAttribute('data-original-html');
        log('Restored original price during discount removal');
      }
    });

    // Remove banner and highlights
    removePromoBanner();
    removeHighlights();

    // Clear tracking
    activeItemIds.clear();
  }

  function checkDiscountCode() {
    log('Checking for discount codes');

    // Only trust visible discount tag
    let tagCode = '';
    const discountTag = document.querySelector('.rebuy-cart__discount-tag');
    if (discountTag && discountTag.offsetParent !== null) {
      tagCode = discountTag.textContent.trim().split(' ')[0].toUpperCase();
    }

    log('Discount code found from tag:', tagCode);

    if (isValid3For2Code(tagCode)) {
      if (!discountActive || currentDiscountCode !== tagCode) {
        log('Valid 3-for-2 code detected:', tagCode);
        currentDiscountCode = tagCode;
        discountActive = true;
        if (window.RebuyUtils?.fetchCart) {
          window.RebuyUtils.fetchCart().then(processCart);
        }
      }
    } else if (discountActive) {
      log('Discount no longer valid — deactivating handler');
      deactivateHandler();
    }
  }

  return {
    init: function () {
      log('Initializing');
      if (!window.RebuyEventManager || !window.RebuyUtils?.fetchCart) return this;
      this.isValid3For2Code = isValid3For2Code;
      window.RebuyEventManager.on('cart:ready', data => setTimeout(checkDiscountCode, 50))
        .on('cart:show', data => setTimeout(checkDiscountCode, 50))
        .on('cart:change', data => {
          log('cart:change event received');
          if (discountActive) setTimeout(() => processCart(data.cart), 100);
        })
        .on('discount:applied', data => {
          log('discount:applied event received');
          setTimeout(checkDiscountCode, 50);
          setTimeout(checkDiscountCode, 750);
        })
        .on('discount:removed', () => {
          log('discount:removed event received');
          deactivateHandler();
        });
      setTimeout(checkDiscountCode, 500);
      return this;
    },
    checkDiscount: function () {
      checkDiscountCode();
      return this;
    },
    deactivate: function () {
      deactivateHandler();
      return this;
    },
    processCart: function (cart) {
      if (!cart && window.RebuyUtils?.fetchCart) {
        window.RebuyUtils.fetchCart().then(processCart);
      } else {
        processCart(cart);
      }
      return this;
    },
    refresh: function () {
      if (discountActive) {
        resetUI();
        window.RebuyUtils.fetchCart().then(processCart);
      }
      return this;
    },
    debug: function (enabled) {
      debug = enabled;
      log('Debug logging', enabled ? 'enabled' : 'disabled');
      return this;
    },
  };
})();
